import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack,
  Visibility,
  Search,
  Email,
  Person,
  Message,
  Reply,
  MarkAsUnread
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  privacy: boolean;
  updates: boolean;
  status: 'new' | 'read' | 'replied';
  createdAt: string;
  updatedAt: string;
}

const ContactManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // Contact'ları yükle
  useEffect(() => {
    loadContacts();
    loadStats();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('https://api.sobcof.com/api/contacts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();

      if (result.success && result.data) {
        setContacts(result.data);
      } else {
        console.error('Error loading contacts:', result.error);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('https://api.sobcof.com/api/contact-stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();

      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const getSubjectIcon = (subject: string) => {
    switch (subject) {
      case 'support': return '🔧';
      case 'account': return '👤';
      case 'premium': return '👑';
      case 'safety': return '🛡️';
      case 'feedback': return '💡';
      case 'business': return '🤝';
      case 'press': return '📰';
      case 'other': return '📋';
      default: return '📧';
    }
  };

  const getSubjectLabel = (subject: string) => {
    switch (subject) {
      case 'support': return 'Teknik Destek';
      case 'account': return 'Hesap Sorunu';
      case 'premium': return 'Premium Üyelik';
      case 'safety': return 'Güvenlik/Raporlama';
      case 'feedback': return 'Öneri/Geri Bildirim';
      case 'business': return 'İş Birliği';
      case 'press': return 'Basın';
      case 'other': return 'Diğer';
      default: return subject;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'error';
      case 'read': return 'warning';
      case 'replied': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new': return 'Yeni';
      case 'read': return 'Okundu';
      case 'replied': return 'Yanıtlandı';
      default: return status;
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesStatus = filterStatus === 'all' || contact.status === filterStatus;
    const matchesSubject = filterSubject === 'all' || contact.subject === filterSubject;
    const matchesSearch = searchTerm === '' ||
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.message.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesStatus && matchesSubject && matchesSearch;
  });

  const handleViewDetails = (contact: Contact) => {
    setSelectedContact(contact);
    setDetailsOpen(true);
    setReplyMessage(''); // Reset reply message

    // Mark as read if it's new
    if (contact.status === 'new') {
      updateContactStatus(contact.id, 'read');
    }
  };

  const handleSendReply = async () => {
    if (!selectedContact || !replyMessage.trim()) return;

    setSendingReply(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`https://api.sobcof.com/api/contacts/${selectedContact.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: replyMessage })
      });

      const result = await response.json();

      if (result.success) {
        // Update local state
        setContacts(prev => prev.map(c =>
          c.id === selectedContact.id ? { ...c, status: 'replied' } : c
        ));

        // Update selected contact
        setSelectedContact(prev => prev ? { ...prev, status: 'replied' } : null);

        loadStats();
        setDetailsOpen(false);
        setReplyMessage('');
        alert('Yanıt başarıyla gönderildi!');
      } else {
        alert('Yanıt gönderilemedi: ' + result.message);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Yanıt gönderilirken bir hata oluştu');
    } finally {
      setSendingReply(false);
    }
  };

  const updateContactStatus = async (contactId: string, newStatus: 'new' | 'read' | 'replied') => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`https://api.sobcof.com/api/contacts/${contactId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setContacts(prev => prev.map(c =>
          c.id === contactId ? { ...c, status: newStatus } : c
        ));
        loadStats(); // Stats'ı yeniden yükle
      }
    } catch (error) {
      console.error('Error updating contact status:', error);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ backgroundColor: '#8B4513' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 2 }}>
            İletişim Mesajları Yönetimi
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Stats Cards */}
        {stats && (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 3,
            mb: 3
          }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Email color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{stats.total}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Mesaj
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <MarkAsUnread color="error" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{stats.new}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Yeni Mesaj
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Visibility color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{stats.read}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Okunmuş
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Reply color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{stats.replied}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Yanıtlanmış
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 200 }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={filterStatus}
                label="Durum"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="new">Yeni</MenuItem>
                <MenuItem value="read">Okundu</MenuItem>
                <MenuItem value="replied">Yanıtlandı</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Konu</InputLabel>
              <Select
                value={filterSubject}
                label="Konu"
                onChange={(e) => setFilterSubject(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="support">Teknik Destek</MenuItem>
                <MenuItem value="account">Hesap Sorunu</MenuItem>
                <MenuItem value="premium">Premium Üyelik</MenuItem>
                <MenuItem value="safety">Güvenlik/Raporlama</MenuItem>
                <MenuItem value="feedback">Öneri/Geri Bildirim</MenuItem>
                <MenuItem value="business">İş Birliği</MenuItem>
                <MenuItem value="press">Basın</MenuItem>
                <MenuItem value="other">Diğer</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Contacts Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tarih</TableCell>
                <TableCell>Gönderen</TableCell>
                <TableCell>E-posta</TableCell>
                <TableCell>Konu</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Mesajlar yükleniyor...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredContacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Henüz mesaj bulunmuyor.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredContacts.map((contact) => (
                  <TableRow key={contact.id} hover>
                    <TableCell>
                      {new Date(contact.createdAt).toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person />
                        {contact.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Email />
                        {contact.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{getSubjectIcon(contact.subject)}</span>
                        {getSubjectLabel(contact.subject)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(contact.status)}
                        color={getStatusColor(contact.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewDetails(contact)}
                      >
                        Görüntüle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedContact && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Message />
                <div>
                  <Typography variant="h6">
                    {getSubjectIcon(selectedContact.subject)} {getSubjectLabel(selectedContact.subject)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedContact.name} • {selectedContact.email} • {' '}
                    {new Date(selectedContact.createdAt).toLocaleString('tr-TR')}
                  </Typography>
                </div>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Gönderen Bilgileri:
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Ad:</strong> {selectedContact.name}<br />
                  <strong>E-posta:</strong> {selectedContact.email}<br />
                  {selectedContact.phone && <><strong>Telefon:</strong> {selectedContact.phone}<br /></>}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  <Message sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Mesaj:
                </Typography>
                <Typography variant="body1" paragraph style={{ whiteSpace: 'pre-line' }}>
                  {selectedContact.message}
                </Typography>
              </Box>

              {selectedContact.status !== 'replied' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      <Reply sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Yanıtla:
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      placeholder="Yanıtınızı buraya yazın..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      disabled={sendingReply}
                    />
                  </Box>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={getSubjectLabel(selectedContact.subject)}
                  icon={<span>{getSubjectIcon(selectedContact.subject)}</span>}
                  size="small"
                />
                <Chip
                  label={getStatusLabel(selectedContact.status)}
                  color={getStatusColor(selectedContact.status) as any}
                  size="small"
                />
                {selectedContact.privacy && (
                  <Chip
                    label="Gizlilik Kabul Edildi"
                    color="success"
                    size="small"
                  />
                )}
                {selectedContact.updates && (
                  <Chip
                    label="Güncellemeler İsteniyor"
                    color="info"
                    size="small"
                  />
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)} disabled={sendingReply}>
                Kapat
              </Button>
              {selectedContact.status !== 'replied' ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSendReply}
                  disabled={!replyMessage.trim() || sendingReply}
                  startIcon={sendingReply ? <CircularProgress size={20} color="inherit" /> : <Reply />}
                >
                  {sendingReply ? 'Gönderiliyor...' : 'Yanıtı Gönder'}
                </Button>
              ) : (
                <Button
                  disabled
                  startIcon={<Reply />}
                  color="success"
                >
                  Yanıtlandı
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ContactManagementPage;
